import React from "react";
import { useApp } from "./store.jsx";
import { api } from "./api.jsx";
import { BackLink, StatRow, LanguageSwitch } from "./chrome.jsx";
import { requirementPhrase, useCopy, inviteUrl } from "./referral.jsx";
import { DrawBanner } from "./reveal.jsx";
import { PrizeChoice } from "./prize-choice.jsx";

const { Button, Badge, Card, Icon, FluteDivider, EmptyState, Skeleton } =
  window.CardboardmarketDesignSystem_2a3239;

/**
 * One draw: what it is, who can enter and how.
 *
 * ITS OWN ADDRESS because it is the thing people send each other. A link that
 * landed on the invite screen would make the reader hunt for what was meant,
 * and a guest opening it has no invite screen to land on.
 *
 * THE RULES ARE BUILT FROM THE CAMPAIGN'S OWN FIELDS, not written out beside
 * it. A prose paragraph in the client would be a second, unversioned copy of
 * the terms — and the moment an operator changed a number the page would be
 * lying about a draw people had already entered.
 */

export function CampaignScreen() {
  const { route, campaigns, campaignsState, loadCampaigns, trader, requireAuth, go, t, locale } = useApp();
  const copy = useCopy();
  const { referral, loadReferral } = useApp();

  React.useEffect(() => { void loadCampaigns(); }, [loadCampaigns]);
  React.useEffect(() => { if (trader) void loadReferral(); }, [trader, loadReferral]);

  const c = campaigns.find((x) => x.id === route.id);

  if (!c) {
    if (campaignsState === 'loading' || campaignsState === 'idle') {
      return <><BackLink /><div className="cbm-stack" style={{ maxWidth: 560 }}>
        <Skeleton height={28} width="60%" /><Skeleton height={48} /><Skeleton height={120} />
      </div></>;
    }
    return (
      <>
        <BackLink />
        <EmptyState icon="search" title={t("This draw is not here")}
                    description={t("It may have finished a while ago, or the link may be wrong.")}
                    action={<Button onClick={() => go('browse')}>{t("Browse cards")}</Button>} />
      </>
    );
  }

  const live = c.state === 'live';
  const day = (iso) => new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'long' });
  const daysLeft = Math.max(0, Math.ceil((new Date(c.endsAt) - Date.now()) / 86400000));
  const shop = c.handoff && c.handoff.type === 'shop';

  const rules = [
    c.withinDays
      ? t("A friend counts once they join with your link or code and {what} within {n} days.", { what: requirementPhrase(c, t), n: c.withinDays })
      : t("A friend counts once they join with your link or code and {what}.", { what: requirementPhrase(c, t) }),
    /*
     * The window a friend has to have JOINED in, which is not the same as the
     * campaign's own dates any more: `lookbackDays` reaches back before it
     * opened, so the sentence has to say the date it actually reaches to.
     */
    t("One entry for each friend who joined between {from} and {to}.", {
      from: day(new Date(new Date(c.startsAt).getTime() - (c.lookbackDays || 0) * 86400000).toISOString()),
      to: day(c.endsAt),
    })
      + (c.capPerTrader ? ' ' + t("Up to {n} entries per person.", { n: c.capPerTrader }) : '')
      + (c.friendEntry ? ' ' + t("Your friend gets one entry too.") : ''),
    /*
      AND THE WAY IN THAT NEEDS NO INVITATION, when the draw offers one.
      Said as its own rule rather than folded into the sentence above, because
      it is the only rule somebody arriving from an advertisement can act on —
      they have nobody to invite yet, and a draw they cannot enter is worth
      knowing about before they read three more lines.
    */
    ...(c.joiningEarnsEntry
      ? [t("Joining counts on its own: sign up, {what}, and you are in the draw.", { what: requirementPhrase(c, t) })]
      : []),
    /*
     * JUST THE DATE. This used to add "if you win, this page will say so",
     * written when nothing else told a winner anything. Both notification kinds
     * exist now, so the sentence had stopped being the whole truth — and the
     * owner's reason for cutting it was simpler and better: a rule list is for
     * what counts, not for how the app behaves afterwards.
     */
    t("{n} winners are drawn on {day}.", { n: c.winners, day: day(c.drawAt) }),
    /*
     * SAID THE WAY IT ACTUALLY WORKS. This promised "the address you give us",
     * which implies a form the product does not have — the arrangement is a
     * message, by hand, as the owner described it: "I manually collect winner's
     * addresses and ship the prize to them." A rule describing machinery that
     * does not exist is the same failure as the privacy line that had to go.
     */
    shop
      ? t("The prize is collected in person, with the code above. No cash alternative.")
      : t("We message the winner for a postal address and send the prize. It is the only thing we ever post."),
    /* The eligibility half only. "A referral is recorded once and never again"
       described our bookkeeping rather than anything a participant can do or
       avoid, and the owner cut it for that. */
    t("Only new accounts count, one per person."),
  ];

  return (
    <>
      <BackLink />
      <div className="cbm-detail" style={{ marginTop: 'var(--space-5)' }}>
        <div className="cbm-stack" style={{ gap: 'var(--space-4)', maxWidth: 420 }}>
          {/*
            The prize, pictured where there is a picture.

            OPTIONAL, AND NOT A GAP WHEN IT IS MISSING. This shipped with no
            photo at all and a card of words instead, because the table had no
            column and a frame reading "prize photo" is a promise the product
            cannot keep. The column exists now, at the owner's ask — but a draw
            still reads perfectly well as words, so the words stay and the
            picture sits above them rather than replacing them.

            `loading="lazy"` and the intrinsic ratio are not decoration: this is
            the largest thing on the page and an unsized image is a layout that
            jumps the moment it lands.
          */}
          {c.image ? (
            <img
              src={c.image.url}
              srcSet={c.image.srcset || undefined}
              sizes="(max-width: 1000px) 100vw, 420px"
              alt=""
              loading="lazy"
              style={{
                width: '100%', aspectRatio: '4 / 3', objectFit: 'cover',
                borderRadius: 'var(--radius-md)', border: '1px solid var(--border-hairline)',
                background: 'var(--kraft-100)',
              }}
            />
          ) : null}
          <Card cut padding="var(--space-6)" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            <span className="cbm-label">{t("The prize")}</span>
            <span style={{ font: 'var(--type-h2)', color: 'var(--text-strong)', textWrap: 'pretty' }}>{c.prize}</span>
            {c.prizeNote ? (
              /* `pre-wrap`, because the note is now a textarea and an operator
                 listing what is on offer expects their line breaks to survive
                 the trip. Without it six prizes render as one paragraph. */
              <span style={{ font: 'var(--type-body-sm)', color: 'var(--text-muted)', whiteSpace: 'pre-wrap' }}>
                {c.prizeNote}
              </span>
            ) : null}
            {/*
              THE OPTIONS, WITH THE PRIZE RATHER THAN WITH THE RESULT.

              This sat in the winner's card, which meant the six boxes an
              operator had typed were invisible to everybody who had not won —
              including to the operator checking their own work. "The prize"
              is where a person looks to find out what is on offer, so the pool
              belongs here; for a winner the same rows become pressable, so
              choosing happens where the choice is described.
            */}
            <PrizeChoice campaign={c} />
          </Card>
        </div>

        <div className="cbm-stack" style={{ gap: 'var(--space-6)' }}>
          <div className="cbm-stack" style={{ gap: 'var(--space-4)' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {live ? <Badge tone="brand">{t("Open")}</Badge>
                : c.state === 'scheduled' ? <Badge tone="neutral">{t("Starts {day}", { day: day(c.startsAt) })}</Badge>
                : <Badge tone="neutral">{t("Ended")}</Badge>}
              <span className="cbm-meta">
                {day(c.startsAt)} – {day(c.endsAt)}{live ? ' · ' + t("{n} days left", { n: daysLeft }) : ''}
              </span>
            </div>
            <h1 className="cbm-h1" style={{ fontSize: 'var(--text-4xl)', letterSpacing: 'var(--tracking-display)', textWrap: 'balance' }}>
              {c.title}
            </h1>
          </div>

          {/*
            THE DRAW HAPPENED AND YOU HAVE NOT LOOKED.

            The third place the design names for this, and the one somebody
            reaches by following a link to the draw itself. Above the result
            card because until it is pressed there IS no result to show.
          */}
          {!c.revealed && c.drawnAt && (c.myEntries || 0) > 0 ? <DrawBanner campaign={c} compact /> : null}

          {/*
            YOU WON, SAID WHERE THE PERSON ALREADY IS.

            Above the numbers, because a winner opening this page has exactly
            one question and the entry counts are not it. Nobody else ever sees
            this block — `youWon` comes back null for everybody but the winner.
            Their NAME is public once they have watched (the list below); the
            pickup code and the rest of this card never are.
          */}
          {c.youWon && c.revealed ? (
            <Card cut padding="var(--space-6)"
                  style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', boxShadow: 'var(--shadow-hard)' }}>
              <span style={{ font: 'var(--type-h3)', color: 'var(--text-strong)' }}>
                {c.youWon.claimedAt ? t("You collected this one.") : t("You won {prize}.", { prize: c.prize })}
              </span>
              {c.youWon.claimedAt ? null : shop && c.youWon.pickupCode ? (
                <>
                  <div className="cbm-pickup">
                    <span className="cbm-label">{t("Pickup code")}</span>
                    <span className="cbm-pickup__code">{c.youWon.pickupCode}</span>
                  </div>
                  <span className="cbm-meta">{t("Show this code when you collect it.")}</span>
                </>
              ) : (
                <span style={{ font: 'var(--type-body-sm)', color: 'var(--text-muted)' }}>
                  {t("We will message you to arrange it.")}
                </span>
              )}
            </Card>
          ) : null}

          <StatRow items={live || c.state === 'ended'
            ? [
                { label: t("Your entries"), value: c.myEntries == null ? '–' : c.myEntries },
                { label: t("Entries so far"), value: c.entries },
                { label: t("Winners"), value: c.winners },
              ]
            : [
                { label: t("Winners"), value: c.winners },
                { label: t("Opens"), value: day(c.startsAt) },
              ]} />

          {/*
            WHO WON, ONCE IT CANNOT SPOIL ANYTHING.

            The owner, on Discord 2026-11-02: "Let's work on showing in a
            campaign all the winners' nicknames. But important, it should not
            spoil it for winners that haven't do their draw." The server
            decides all of it: a place whose winner has not watched comes back
            nameless, and somebody who was in the draw and has not watched gets
            no list at all — so the banner above stands alone until they do.
            The screen does not re-derive that rule: it shows what came back,
            and draws skeletons only in the moment between an entrant's reveal
            being written and the refetch landing. A draw nobody won has no
            section rather than a heading over nothing.
          */}
          {c.drawnAt && (c.winnersList ? c.winnersList.length > 0 : c.revealed && (c.myEntries || 0) > 0)
            ? <WinnersList campaign={c} /> : null}

          {live ? (
            <div className="cbm-actionbar">
              {!trader
                ? <Button size="lg" onClick={() => requireAuth(t("take part in the draw"))}>{t("Join to take part")}</Button>
                : <Button size="lg" iconLeft="user" onClick={() => go('invite')}>{t("Invite friends")}</Button>}
              {trader && referral && referral.code ? (
                <Button size="lg" variant="outline"
                        onClick={() => copy(inviteUrl(referral.code), t("Link copied. Paste it wherever your friends are."))}>
                  {t("Copy your link")}
                </Button>
              ) : null}
            </div>
          ) : null}

          <FluteDivider />
          <section className="cbm-stack" style={{ gap: 'var(--space-4)' }}>
            <span className="cbm-label">{t("How the draw works")}</span>
            <ol className="cbm-rules">
              {rules.map((r, i) => (
                <li key={i}><span className="cbm-meta">{String(i + 1).padStart(2, '0')}</span><span>{r}</span></li>
              ))}
            </ol>
          </section>
        </div>
      </div>
    </>
  );
}

/**
 * The places of a drawn campaign, one line each.
 *
 * A NAMELESS PLACE IS STILL A LINE, the same height as a named one: the place
 * is there, its winner simply has not looked yet, and filling it in later
 * must not move the rules below.
 *
 * `winnersList` can be null for a moment after somebody watches their own
 * result — the store refetches once the reveal is written — so until it lands
 * the lines are drawn as skeletons of the same size, not as nothing. One per
 * place OFFERED: the number actually filled is not known yet, and asking the
 * server for it before the reveal would say "everybody won" in a short draw.
 */
function WinnersList({ campaign: c }) {
  const { t } = useApp();
  const list = c.winnersList;
  const label = (n) => String(n).padStart(2, '0');
  return (
    <section className="cbm-stack" style={{ gap: 'var(--space-4)' }} data-testid="campaign-winners">
      <span className="cbm-label">{t("Winners")}</span>
      <ol className="cbm-rules">
        {list == null
          ? Array.from({ length: c.winners }, (_, i) => (
              <li key={i}><span className="cbm-meta">{label(i + 1)}</span><Skeleton height={20} width="50%" /></li>
            ))
          : list.map((w) => (
              <li key={w.place} data-status={w.status}>
                <span className="cbm-meta">{label(w.place)}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 20, flexWrap: 'wrap' }}>
                  {w.status === 'revealed' ? (
                    /* The @handle, as every other screen writes it — the
                       owner: "We should show the nickname. The @ handle".
                       Not a link: a deleted account's place carries a
                       made-up handle, and a link that went nowhere for it
                       alone would say what the made-up handle exists to
                       keep quiet. */
                    <>
                      <span style={{ color: 'var(--text-strong)' }}>@{w.handle}</span>
                      {w.isYou ? <Badge tone="brand">{t("You")}</Badge> : null}
                    </>
                  ) : (
                    /* The blank line the owner asked for: a rule where the
                       name will go, and what it is waiting on. */
                    <>
                      <span aria-hidden="true" style={{ width: 96, borderBottom: '1px dashed var(--border-strong)', alignSelf: 'flex-end', marginBottom: 4 }} />
                      <span className="cbm-meta">{t("Waiting for their draw")}</span>
                    </>
                  )}
                </span>
              </li>
            ))}
      </ol>
    </section>
  );
}

/**
 * What a friend opens.
 *
 * It does not sign anybody up itself. The account is made by the onboarding
 * flow, which is where the password rules, the confirmation and four other
 * steps live — a second signup form here would be a second one to keep
 * correct, and this feature is not worth that. This page says who invited you
 * and what it gets you, holds the code, and hands over.
 *
 * THE CODE IS TAKEN FROM THE ADDRESS AND VERIFIED BEFORE IT IS SHOWN. A code
 * that belongs to nobody comes back as nothing — not as an error — so the page
 * simply becomes an ordinary welcome rather than naming a person who does not
 * exist.
 */
export function InvitedScreen() {
  const { route, trader, go, setInviteCode, campaigns, loadCampaigns, liveCampaign, t } = useApp();
  const [inviter, setInviter] = React.useState(undefined);   // undefined = still asking
  const code = (route.id || '').toUpperCase();

  React.useEffect(() => { void loadCampaigns(); }, [loadCampaigns]);

  React.useEffect(() => {
    let live = true;
    setInviter(undefined);
    api.inviter(code)
      .then((r) => { if (live) setInviter(r.inviter); })
      .catch(() => { if (live) setInviter(null); });
    return () => { live = false; };
  }, [code]);

  /*
   * Held the moment the page opens, not when the button is pressed.
   *
   * Somebody can read this page, wander off to browse, and come back to sign up
   * an hour later — the code has to survive that, and the store is where things
   * survive a navigation. It is spent by `signup` and cleared there.
   *
   * Never for somebody already signed in: a referral belongs to the account's
   * first moment, and applying one afterwards is the thing the whole feature
   * refuses to do.
   */
  React.useEffect(() => {
    if (!trader && code) setInviteCode(code);
  }, [trader, code, setInviteCode]);

  const c = liveCampaign;

  /*
   * SIGNED IN, ON SOMEBODY'S INVITE LINK.
   *
   * This rendered as a preview, with a bar across the top saying so and a link
   * on the invite screen to reach it. Both are gone at the owner's ask. What is
   * left is the honest behaviour for the case: a referral belongs to an
   * account's FIRST moment, so this page has nothing to offer somebody who
   * already has one. Send them to browse, replacing rather than pushing — a
   * back button that returns you to a page you were bounced off is a trap.
   */
  React.useEffect(() => {
    if (trader) go('browse', undefined, { replace: true });
  }, [trader, go]);
  if (trader) return null;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)' }}>
      <div className="cbm-onb">
        <aside className="cbm-onb__brand">
          <h1 style={{ margin: 'auto 0 0', font: 'var(--type-display)', fontSize: 'clamp(38px,5vw,66px)', letterSpacing: 'var(--tracking-display)', color: 'var(--kraft-50)', textTransform: 'lowercase' }}>
            {inviter ? t("{name} invited you to trade", { name: inviter }) : t("cards near you, not in the post")}
          </h1>
          <p style={{ margin: '20px 0 0', font: 'var(--type-body)', color: 'var(--kraft-300)', maxWidth: 420 }}>
            {t("Cardboardmarket matches you with card holders a few streets away. You meet somewhere public, check the card and hand it over. No posting.")}
          </p>
          {c && c.friendEntry ? (
            <>
              <FluteDivider tone="brand" style={{ margin: '32px 0 20px' }} />
              <div className="cbm-stack" style={{ gap: 'var(--space-4)' }}>
                <span className="cbm-label" style={{ color: 'var(--kraft-400)' }}>
                  {t("When you {what}", { what: requirementPhrase(c, t) })}
                </span>
                <div className="cbm-stack" style={{ gap: 4 }}>
                  <span style={{ font: 'var(--type-price)', fontSize: 'var(--text-lg)', color: 'var(--orange-300)' }}>
                    {t("1 entry")}
                  </span>
                  <span className="cbm-label" style={{ color: 'var(--kraft-400)' }}>
                    {t("in the draw for {prize}", { prize: c.prize })}
                  </span>
                </div>
              </div>
            </>
          ) : null}
        </aside>
        <main className="cbm-onb__form">
          <div style={{ width: '100%', maxWidth: 440, display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
            {/*
              THE ONLY WAY OUT OF THE WRONG LANGUAGE ON THIS PAGE.

              Reported: the invite landing page had no switch. It takes the whole
              window like onboarding does, so there is no header carrying one —
              and this is the FIRST thing a stranger ever sees of the product,
              opened from somebody else's link, frequently on a browser whose
              language we guessed wrong. Somebody reading a language they do not
              speak is exactly the person least able to hunt for the control.
            */}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}><LanguageSwitch /></div>
            <h2 className="cbm-h2">
              {inviter ? t("Join with {name}’s code", { name: inviter }) : t("Join Cardboardmarket")}
            </h2>
            <p className="cbm-sub" style={{ margin: 0 }}>
              {t("Takes a minute. Next you set a point and a radius, and see what is listed around you.")}
            </p>
            <div className="cbm-linkbox">
              <code>{code}</code>
              {inviter !== undefined ? (
                <span className="cbm-meta">{inviter ? t("Applied") : t("Not a code we know")}</span>
              ) : null}
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap' }}>
              <Button size="lg" onClick={() => go('onboarding')}>{t("Join")}</Button>
              <Button size="lg" variant="ghost" onClick={() => go('browse')}>{t("Look around first")}</Button>
            </div>
            {/*
              NO PRIVACY PROMISE HERE, because the one that was here was false.
              It said the inviter "only sees that you joined and whether you
              have posted. Never your location or your cards." Checked against
              the API with no cookie at all: `/api/traders/:handle` and
              `/api/traders/:handle/listings` both answer a total stranger with
              the profile and every active listing. A signed-in inviter with a
              point of their own also gets a distance.

              All of that is the product working as designed — a marketplace
              where you look at what somebody has and how far away they are —
              and none of it is a secret being kept. Reported by the owner:
              "that's incorrect... I would just remove the whole line."

              A sentence that overstates privacy is worse than none, because
              the person it misleads is the one deciding whether to join.
            */}
          </div>
        </main>
      </div>
    </div>
  );
}
